import {
    type ColorPalette,
    DEFAULT_BACKGROUND_COLOR,
    DEFAULT_BACKGROUND_DERIVATION,
    DEFAULT_COLOR_DERIVATION,
    DEFAULT_TEXT_DERIVATION,
    designSystemColorPalettes,
    prettyFeelColorPalettes,
    type PrettyFeelSharedUiColorName,
    type RgbColorHex,
    SharedUiBackgroundColorDerivation,
    SharedUiBackgroundColorName,
    type SharedUiBackgroundColorOptions,
    SharedUiColorDerivation,
    type SharedUiColoredBackgroundColorOptions,
    SharedUiColorName,
    SharedUiColorNameV2,
    type SharedUiColorOptions,
    type SharedUiColorsService,
    SharedUiStrokeColorName,
    SharedUiTextColorDerivation,
    stringToRgbColor
} from '@/shared/ui/theming';
import { Memoize } from '@/utils/toolkit/memoize.decorator';
import { Injectable } from '@angular/core';
import { type Observable, of } from 'rxjs';
import { primaryColor, secondaryColor, sseTurquoiseColor, sseTurquoiseLightColor } from '../constants/color';

const designSystemColors: Record<SharedUiColorName, Record<SharedUiColorDerivation, RgbColorHex>> = {
    [SharedUiColorName.CTA]: {
        [SharedUiColorDerivation.MAIN]: stringToRgbColor('#0048FF'),
        [SharedUiColorDerivation.LIGHT]: stringToRgbColor('#EFF4FF'),
        [SharedUiColorDerivation.DARK]: stringToRgbColor('#0033B7'),
        [SharedUiColorDerivation.CONTRAST]: stringToRgbColor('#FFFFFF')
    },
    [SharedUiColorName.INFO]: {
        [SharedUiColorDerivation.MAIN]: stringToRgbColor('#A5BFFF'),
        [SharedUiColorDerivation.LIGHT]: stringToRgbColor('#EAF0FF'),
        [SharedUiColorDerivation.DARK]: stringToRgbColor('#4A75D7'),
        [SharedUiColorDerivation.CONTRAST]: stringToRgbColor('#03102B')
    },
    [SharedUiColorName.DANGER]: {
        [SharedUiColorDerivation.MAIN]: stringToRgbColor('#FFB429'),
        [SharedUiColorDerivation.LIGHT]: stringToRgbColor('#FBEFD0'),
        [SharedUiColorDerivation.DARK]: stringToRgbColor('#DD5509'),
        [SharedUiColorDerivation.CONTRAST]: stringToRgbColor('#402F04')
    },
    [SharedUiColorName.SUCCESS]: {
        [SharedUiColorDerivation.MAIN]: stringToRgbColor('#7cc796'),
        [SharedUiColorDerivation.LIGHT]: stringToRgbColor('#B7FFD0'),
        [SharedUiColorDerivation.DARK]: stringToRgbColor('#1B7938'),
        [SharedUiColorDerivation.CONTRAST]: stringToRgbColor('#032D12')
    },
    [SharedUiColorName.FAILURE]: {
        [SharedUiColorDerivation.MAIN]: stringToRgbColor('#F94B2D'),
        [SharedUiColorDerivation.LIGHT]: stringToRgbColor('#FAE6E3'),
        [SharedUiColorDerivation.DARK]: stringToRgbColor('#B7240B'),
        [SharedUiColorDerivation.CONTRAST]: stringToRgbColor('#FFFFFF')
    },
    [SharedUiColorName.NEUTRAL]: {
        [SharedUiColorDerivation.MAIN]: stringToRgbColor('#DFE0E3'),
        [SharedUiColorDerivation.LIGHT]: stringToRgbColor('#F5F6F8'),
        [SharedUiColorDerivation.DARK]: stringToRgbColor('#5C616B'),
        [SharedUiColorDerivation.CONTRAST]: stringToRgbColor('#4B505B')
    },
    [SharedUiColorName.PRIMARY]: {
        [SharedUiColorDerivation.MAIN]: stringToRgbColor(primaryColor),
        [SharedUiColorDerivation.LIGHT]: stringToRgbColor(secondaryColor),
        [SharedUiColorDerivation.DARK]: stringToRgbColor('#a04432'),
        [SharedUiColorDerivation.CONTRAST]: stringToRgbColor('#000000')
    },
    [SharedUiColorName.SECONDARY]: {
        [SharedUiColorDerivation.MAIN]: stringToRgbColor(sseTurquoiseColor),
        [SharedUiColorDerivation.LIGHT]: stringToRgbColor(sseTurquoiseLightColor),
        [SharedUiColorDerivation.DARK]: stringToRgbColor('#75958f'),
        [SharedUiColorDerivation.CONTRAST]: stringToRgbColor('#000000')
    }
};

const designSystemBackgroundColors: Record<SharedUiBackgroundColorName, Record<SharedUiBackgroundColorDerivation, RgbColorHex>> = {
    [SharedUiBackgroundColorName.LIGHT]: {
        [SharedUiBackgroundColorDerivation.MAIN]: stringToRgbColor('#FFFFFF'),
        [SharedUiBackgroundColorDerivation.FADED]: stringToRgbColor('#F5F6F8'),
        [SharedUiBackgroundColorDerivation.HIGHLIGHT]: stringToRgbColor('#DFE0E3')
    },
    [SharedUiBackgroundColorName.DARK]: {
        [SharedUiBackgroundColorDerivation.MAIN]: stringToRgbColor('#323A40'),
        [SharedUiBackgroundColorDerivation.FADED]: stringToRgbColor('#4B505B'),
        [SharedUiBackgroundColorDerivation.HIGHLIGHT]: stringToRgbColor('#5C616B')
    }
};

const designSystemTextColors: Record<SharedUiTextColorDerivation, Record<SharedUiBackgroundColorName, RgbColorHex>> = {
    [SharedUiTextColorDerivation.MAIN]: {
        [SharedUiBackgroundColorName.LIGHT]: stringToRgbColor('#4B505B'),
        [SharedUiBackgroundColorName.DARK]: stringToRgbColor('#FFFFFF')
    },
    [SharedUiTextColorDerivation.SECONDARY]: {
        [SharedUiBackgroundColorName.LIGHT]: stringToRgbColor('#888C95'),
        [SharedUiBackgroundColorName.DARK]: stringToRgbColor('#F5F6F8') // Missing in current design system
    },
    [SharedUiTextColorDerivation.FOCUSED]: {
        [SharedUiBackgroundColorName.LIGHT]: stringToRgbColor('#0033B7'),
        [SharedUiBackgroundColorName.DARK]: stringToRgbColor('#0033B7') // Missing in current design system
    }
};

const designSystemStrokeColors: Record<SharedUiStrokeColorName, RgbColorHex> = {
    [SharedUiStrokeColorName.LIGHT]: stringToRgbColor('#C5C8CD'),
    [SharedUiStrokeColorName.DARK]: stringToRgbColor('#4B505B')
};

@Injectable({
    providedIn: 'root'
})
export class AdvizeoColorsService implements SharedUiColorsService {
    @Memoize()
    getColor$(colorName: SharedUiColorName, { derivation = DEFAULT_COLOR_DERIVATION }: SharedUiColorOptions = {}): Observable<RgbColorHex> {
        return of(designSystemColors[colorName][derivation]);
    }

    // NORMAL BACKGROUNDS

    @Memoize()
    getBackgroundColor$({
        color = DEFAULT_BACKGROUND_COLOR,
        derivation = DEFAULT_BACKGROUND_DERIVATION
    }: SharedUiBackgroundColorOptions = {}): Observable<RgbColorHex> {
        return of(designSystemBackgroundColors[color][derivation]);
    }

    @Memoize()
    getTextColor$(
        derivation: SharedUiTextColorDerivation = DEFAULT_TEXT_DERIVATION,
        { color: backgroundColor = DEFAULT_BACKGROUND_COLOR }: SharedUiBackgroundColorOptions = {}
    ): Observable<RgbColorHex> {
        return of(designSystemTextColors[derivation][backgroundColor]);
    }

    @Memoize()
    getColoredTextColor$(
        colorName: SharedUiColorName,
        { color: backgroundColor = DEFAULT_BACKGROUND_COLOR }: SharedUiBackgroundColorOptions = {}
    ): Observable<RgbColorHex> {
        // Need to fix hexa color in app_dashboard_widget_ref.csv
        if (!designSystemColors[colorName]) {
            return of(colorName as RgbColorHex);
        }
        if (backgroundColor === SharedUiBackgroundColorName.LIGHT) {
            return of(designSystemColors[colorName][SharedUiColorDerivation.MAIN]);
        } else {
            return of(designSystemColors[colorName][SharedUiColorDerivation.LIGHT]);
        }
    }

    @Memoize()
    getStrokeColor$({ color: backgroundColor = DEFAULT_BACKGROUND_COLOR }: SharedUiBackgroundColorOptions = {}): Observable<RgbColorHex> {
        return of(
            backgroundColor === SharedUiBackgroundColorName.LIGHT
                ? designSystemStrokeColors[SharedUiStrokeColorName.LIGHT]
                : designSystemStrokeColors[SharedUiStrokeColorName.DARK]
        );
    }

    @Memoize()
    getColoredBackgroundColor$({
        color,
        derivation = DEFAULT_COLOR_DERIVATION
    }: SharedUiColoredBackgroundColorOptions): Observable<RgbColorHex> {
        return of(designSystemColors[color][derivation]);
    }

    @Memoize()
    getTextColorOnColoredBackground$({
        color: backgroundColor,
        derivation: backgroundDerivation = DEFAULT_COLOR_DERIVATION
    }: SharedUiColoredBackgroundColorOptions): Observable<RgbColorHex> {
        if (backgroundDerivation === SharedUiColorDerivation.MAIN) {
            return of(designSystemColors[backgroundColor][SharedUiColorDerivation.CONTRAST]);
        } else if (backgroundDerivation === SharedUiColorDerivation.LIGHT) {
            return of(designSystemColors[backgroundColor][SharedUiColorDerivation.DARK]);
        } else {
            return of(designSystemColors[backgroundColor][SharedUiColorDerivation.LIGHT]);
        }
    }

    getColorPalette(color: SharedUiColorNameV2 | PrettyFeelSharedUiColorName): ColorPalette {
        if ((Object.values(SharedUiColorNameV2) as string[]).includes(color)) {
            return designSystemColorPalettes[color as SharedUiColorNameV2];
        } else {
            return prettyFeelColorPalettes[color as PrettyFeelSharedUiColorName];
        }
    }

    getAllPrettyFeelColorPalette(): ColorPalette[] {
        return Object.values(prettyFeelColorPalettes);
    }
}
